import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useApiControl from '../../common/useApiControl';
import { clearRoomCacheAndGoTop } from '../../common/roomFatalError';
import BetStartButton from '../buttons/betStartButton';
import RoundFinishButton from '../buttons/roundFinishButton';
import EventCheckOutButton from '../buttons/eventCheckOutButton';
import SaveRoundResultDialog from '../dialogs/SaveRoundResultDialog';
import OwnerConfirmRoundResultDialog from '../dialogs/OwnerConfirmRoundResultDialog';
import ConfirmRoundResultDialog from '../dialogs/ConfirmRoundResultDialog';
import ResultCorrectionDialog from '../dialogs/ResultCorrectionDialog';
import CheckEventCheckOutDialog from '../dialogs/CheckEventCheckOutDialog';
import OwnerCheckoutSummaryDialog from '../dialogs/OwnerCheckoutSummaryDialog';
import OwnerResultDialog from '../dialogs/OwnerResultDialog';
import PlannedRoundDecisionDialog from '../dialogs/PlannedRoundDecisionDialog';
import { DIALOG_MODE } from '../dialogs/BaseDialog';
import HelpButton from '../headers/HelpButton';
import {
  getServerPhaseCommandFromSseData,
  ROOM_DISPLAY_PHASE,
  SERVER_PHASE,
} from '../room/useRoomPhase';
import { OWNER_CONSOLE_DISPLAY_CATEGORY, OWNER_CONSOLE_DISPLAY_LABEL } from './ownerConsoleDisplayMeta';
import { CHAT_MESSAGE_MAX_LENGTH } from '../room/useRoomChat';
import useOwnerConsoleTimeSignals from './useOwnerConsoleTimeSignals';
import { normalizePlannedRoundCount } from '../../common/plannedRoundCount';

const PREPARE_RETRY_DELAY_MS = 3000;
const RESULT_CONFIRMATION_METHOD_OWNER = 'owner_confirmation';
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const parseChoices = (answers) => {
  if (Array.isArray(answers)) return answers;
  if (!answers) return [];
  try {
    const parsed = JSON.parse(answers);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Error parsing answers JSON', error);
    return [];
  }
};

const formatPoint = (value) => `${Number(value || 0).toLocaleString()} pt`;
const formatRemaining = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return '--:--';
  const safeSeconds = Math.max(0, Math.ceil(Number(seconds)));
  return `${Math.floor(safeSeconds / 60).toString().padStart(2, '0')}:${(safeSeconds % 60).toString().padStart(2, '0')}`;
};

const getNextActionGuide = (category) => {
  switch (category) {
    case OWNER_CONSOLE_DISPLAY_CATEGORY.WAITING:
      return '参加者と選択肢が揃ったら、ベット受付を開始してください。';
    case OWNER_CONSOLE_DISPLAY_CATEGORY.BETTING:
      return 'ベット受付中です。受付終了後、結果を登録します。';
    case OWNER_CONSOLE_DISPLAY_CATEGORY.RESULT_INPUT:
      return 'ベット受付が終了しました。結果を登録してください。';
    case OWNER_CONSOLE_DISPLAY_CATEGORY.RESULT_REVIEW:
      return '参加者の確認を待っています。再確認依頼が届いた場合は、内容を確認して結果を修正または確定してください。';
    case OWNER_CONSOLE_DISPLAY_CATEGORY.ROUND_RESULT:
      return '結果確定済みです。次ラウンドを開始できます。次ラウンドを開始せずルームを終了することもできます。';
    case OWNER_CONSOLE_DISPLAY_CATEGORY.COMPLETED:
      return 'ルームは終了済みです。最終結果を確認できます。';
    default:
      return 'イベントまたはラウンド状態を取得中です。取得完了後に操作できます。';
  }
};

const getPhaseHelper = (category) => {
  switch (category) {
    case OWNER_CONSOLE_DISPLAY_CATEGORY.BETTING:
      return '視聴者はベット中です。';
    case OWNER_CONSOLE_DISPLAY_CATEGORY.RESULT_REVIEW:
      return '確定前の確認段階です。確定済みとは異なります。';
    case OWNER_CONSOLE_DISPLAY_CATEGORY.ROUND_RESULT:
      return 'このラウンドの結果は確定済みです。';
    default:
      return 'このエリアは配信に映さないオーナー向け操作卓です。';
  }
};

const shouldShowBetInfo = (category) => [
  OWNER_CONSOLE_DISPLAY_CATEGORY.BETTING,
  OWNER_CONSOLE_DISPLAY_CATEGORY.RESULT_INPUT,
  OWNER_CONSOLE_DISPLAY_CATEGORY.RESULT_REVIEW,
  OWNER_CONSOLE_DISPLAY_CATEGORY.ROUND_RESULT,
].includes(category);

const shouldShowResultInfo = (category) => [
  OWNER_CONSOLE_DISPLAY_CATEGORY.RESULT_INPUT,
  OWNER_CONSOLE_DISPLAY_CATEGORY.RESULT_REVIEW,
  OWNER_CONSOLE_DISPLAY_CATEGORY.ROUND_RESULT,
].includes(category);

function InfoCard({ label, value, helper, tone = 'default' }) {
  return (
    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: tone === 'accent' ? '#f0fdfa' : '#fff', border: tone === 'accent' ? '1px solid #99f6e4' : '1px solid #e2e8f0', minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 900 }}>{label}</Typography>
      <Typography variant="h6" fontWeight={900} sx={{ wordBreak: 'break-word' }}>{value}</Typography>
      {helper ? <Typography variant="body2" sx={{ color: '#64748b' }}>{helper}</Typography> : null}
    </Box>
  );
}

function CountdownChip({ event, round }) {
  const timeSignals = useOwnerConsoleTimeSignals(round, undefined, event?.bet_close_mode);
  const isManualBetClose = event?.bet_close_mode === 'manual' && timeSignals?.countdownTarget !== 'start';
  const remaining = isManualBetClose
    ? timeSignals?.elapsedSeconds
    : (timeSignals?.countdownSeconds ?? timeSignals?.remainingSeconds);
  const label = timeSignals?.countdownTarget === 'start'
    ? '開始まで'
    : (isManualBetClose ? '経過' : '残時間');

  return <Chip label={`${label} ${formatRemaining(remaining)}`} />;
}

function CountdownInfoCard({ event, round }) {
  const timeSignals = useOwnerConsoleTimeSignals(round, undefined, event?.bet_close_mode);
  const isManualBetClose = event?.bet_close_mode === 'manual' && timeSignals?.countdownTarget !== 'start';
  const remaining = isManualBetClose
    ? timeSignals?.elapsedSeconds
    : (timeSignals?.countdownSeconds ?? timeSignals?.remainingSeconds);
  const label = timeSignals?.countdownTarget === 'start'
    ? '開始まで'
    : (isManualBetClose ? '経過時間' : '残時間');

  return <InfoCard label={label} value={formatRemaining(remaining)} />;
}

function OwnerControlAreaMobileLayout({ sections }) {
  return (
    <Stack spacing={2.25}>
      {sections.header}
      {sections.settings}
      {sections.message}
      {sections.startError}
      {sections.statusSummary}
      {sections.action}
      {sections.chat}
      {sections.roundInfo}
      {sections.betInfo}
      {sections.resultInfo}
      {sections.checkout}
      {sections.operationMessage}
    </Stack>
  );
}

function OwnerControlAreaDesktopLayout({ sections }) {
  return (
    <Stack spacing={1.5}>
      {sections.header}
      {sections.message}
      {sections.startError}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(320px, 0.85fr)', gap: 1.5, alignItems: 'start' }}>
        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
          {sections.action}
          {sections.roundInfo}
          {sections.resultInfo}
        </Stack>
        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
          {sections.statusSummary}
          {sections.settings}
          {sections.betInfo}
        </Stack>
        <Box sx={{ gridColumn: '1 / -1' }}>
          {sections.chat}
        </Box>
      </Box>
      {sections.checkout}
      {sections.operationMessage}
    </Stack>
  );
}

export default function CoveredControlArea({ mobile = false, event, roomId, round, phaseState, onRefresh, liveSummary, sseData, chat, review, reviewRefreshKey = 0 }) {
  const controlAreaRef = useRef(null);
  const checkoutSectionRef = useRef(null);
  const category = phaseState?.displayCategory ?? OWNER_CONSOLE_DISPLAY_CATEGORY.UNRESOLVED;
  const choices = useMemo(() => parseChoices(round?.answers), [round?.answers]);
  const savedResult = round?.result !== null && round?.result !== undefined ? String(round.result) : '';
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [participantsCount, setParticipantsCount] = useState(null);
  const [message, setMessage] = useState(null);
  const [roundTransitionNotice, setRoundTransitionNotice] = useState(null);
  const [saveRoundResultDialogVisible, setSaveRoundResultDialogVisible] = useState(false);
  const [ownerConfirmRoundResultDialogVisible, setOwnerConfirmRoundResultDialogVisible] = useState(false);
  const [ownerConfirmResultReady, setOwnerConfirmResultReady] = useState(false);
  const [confirmRoundResultDialogVisible, setConfirmRoundResultDialogVisible] = useState(false);
  const [resultCorrectionDialogVisible, setResultCorrectionDialogVisible] = useState(false);
  const [checkEventCheckOutDialogVisible, setCheckEventCheckOutDialogVisible] = useState(false);
  const [checkoutCompletionReason, setCheckoutCompletionReason] = useState('owner_ended');
  const [plannedRoundDecisionDialogVisible, setPlannedRoundDecisionDialogVisible] = useState(false);
  const [ownerCheckoutSummaryDialogVisible, setOwnerCheckoutSummaryDialogVisible] = useState(false);
  const [ownerResultDialogVisible, setOwnerResultDialogVisible] = useState(false);
  const [ownerResultDialogDatas, setOwnerResultDialogDatas] = useState(null);
  const [dialogRound, setDialogRound] = useState(event?.round ?? round?.number ?? 1);
  const dialogRoundRef = useRef(event?.round ?? round?.number ?? 1);
  const [checkoutExpanded, setCheckoutExpanded] = useState(false);
  const [finishButtonResetKey, setFinishButtonResetKey] = useState(0);
  const [startButtonsLocked, setStartButtonsLocked] = useState(false);
  const [startErrorMessage, setStartErrorMessage] = useState('');
  const [chatInput, setChatInput] = useState('');
  const completedDialogOpenedRef = useRef(false);
  const completedDialogDismissedRef = useRef(false);
  const plannedDecisionDismissedKeyRef = useRef(null);
  const ownerConfirmDialogAutoOpenedKeyRef = useRef(null);

  const refresh = async () => {
    await onRefresh?.();
    setLastUpdatedAt(new Date());
  };

  const fetchUsersParams = useMemo(() => ({ event_id: event?.id }), [event?.id]);
  const ownerResultParams = useMemo(
    () => ({ event_id: event?.id, number: dialogRound }),
    [event?.id, dialogRound]
  );
  const prepareSettlementParams = useMemo(() => ({ event_id: event?.id }), [event?.id]);

  const { executeRequest: fetchUsers } = useApiControl({
    action: '/get_event_users',
    params: fetchUsersParams,
    method: 'POST',
  });
  const { executeRequest: fetchOwnerResult } = useApiControl({
    action: '/get_round_result',
    params: ownerResultParams,
    method: 'POST',
  });
  const { executeRequest: prepareSettlement } = useApiControl({
    action: '/prepare_round_settlement',
    params: prepareSettlementParams,
    method: 'POST',
  });
  const plannedRoundCount = normalizePlannedRoundCount(event?.planned_round_count);
  const isOwnerConfirmation = event?.result_confirmation_method === RESULT_CONFIRMATION_METHOD_OWNER;
  const confirmedRoundCount = Number(event?.confirmed_round_count ?? 0);
  const plannedRoundDecisionKey = event?.id && confirmedRoundCount > 0
    ? `${event.id}:${confirmedRoundCount}`
    : null;
  const isCompletedEvent = Boolean((event?.completion_status && event.completion_status !== 'active') || phaseState?.roomDisplayPhase === ROOM_DISPLAY_PHASE.COMPLETED);
  const shouldShowPlannedRoundDecision = Boolean(
    event?.id &&
    plannedRoundDecisionKey &&
    !isCompletedEvent &&
    confirmedRoundCount >= plannedRoundCount &&
    [
      OWNER_CONSOLE_DISPLAY_CATEGORY.ROUND_RESULT,
    ].includes(category)
  );

  const updateDialogRound = (value) => {
    const nextValue = Number(value) || 1;
    dialogRoundRef.current = nextValue;
    setDialogRound(nextValue);
  };

  const resolveOwnerResultRoundNumber = (payloadRoundNumber) => {
    const payloadNumber = Number(payloadRoundNumber);
    if (Number.isInteger(payloadNumber) && payloadNumber > 1) {
      // CONFIRMED SSEを受ける時点ではDB上のevent.roundが次ラウンドへ進んでいる。
      // OwnerResultDialogで取得したいのは確定済みの直前ラウンドなので、SSEのround_numberから1戻す。
      return payloadNumber - 1;
    }

    if (Number.isInteger(confirmedRoundCount) && confirmedRoundCount > 0) {
      return confirmedRoundCount;
    }

    const currentRoundNumber = Number(round?.number);
    return Number.isInteger(currentRoundNumber) && currentRoundNumber > 0 ? currentRoundNumber : dialogRoundRef.current;
  };

  useEffect(() => {
    updateDialogRound(event?.round ?? round?.number ?? 1);
  }, [event?.id]);

  const openOwnerResultDialog = async (roundNumber = dialogRoundRef.current) => {
    if (!event?.id || !roundNumber) return;

    try {
      const result = await fetchOwnerResult({
        event_id: event.id,
        number: roundNumber,
      });
      setOwnerResultDialogDatas({
        result,
        event_id: event.id,
      });
      updateDialogRound(roundNumber);
      setOwnerResultDialogVisible(true);
    } catch (error) {
      setMessage({
        severity: 'warning',
        text: 'ラウンド結果の取得に失敗しました。',
      });
    }
  };

  const openSaveRoundResultDialog = async (visible) => {
    if (!visible) {
      setSaveRoundResultDialogVisible(false);
      setOwnerConfirmRoundResultDialogVisible(false);
      return;
    }

    try {
      setMessage(null);
      const result = await prepareSettlement();
      if (result?.status === 'invalid' && result?.reason === 'one_sided_bets') {
        setSaveRoundResultDialogVisible(false);
        setOwnerConfirmRoundResultDialogVisible(false);
        setConfirmRoundResultDialogVisible(false);
        setMessage(null);
        setRoundTransitionNotice({
          severity: 'info',
          text: `ROUND ${round?.number ?? event?.round ?? '-'} は不成立となりました。ベットしたルームチップを返還しました。`,
        });
        await refresh();
        fetchUsers().then((data) => {
          const users = data?.all;
          if (Array.isArray(users)) setParticipantsCount(users.length);
        }).catch(() => {});
        return;
      }
      if (isOwnerConfirmation) {
        setOwnerConfirmResultReady(true);
        setOwnerConfirmRoundResultDialogVisible(true);
      } else {
        setSaveRoundResultDialogVisible(true);
      }
    } catch (error) {
      if (error?.response?.status === 409) {
        setMessage({
          severity: 'info',
          text: 'サーバーと同期しています。しばらくお待ちください。',
        });
        await wait(PREPARE_RETRY_DELAY_MS);

        try {
          const result = await prepareSettlement();
          if (result?.status === 'invalid' && result?.reason === 'one_sided_bets') {
            setSaveRoundResultDialogVisible(false);
            setOwnerConfirmRoundResultDialogVisible(false);
            setConfirmRoundResultDialogVisible(false);
            setMessage(null);
            setRoundTransitionNotice({
              severity: 'info',
              text: `ROUND ${round?.number ?? event?.round ?? '-'} は不成立となりました。ベットしたルームチップを返還しました。`,
            });
            await refresh();
            fetchUsers().then((data) => {
              const users = data?.all;
              if (Array.isArray(users)) setParticipantsCount(users.length);
            }).catch(() => {});
            return;
          }
          setMessage(null);
          if (isOwnerConfirmation) {
            setOwnerConfirmResultReady(true);
            setOwnerConfirmRoundResultDialogVisible(true);
          } else {
            setSaveRoundResultDialogVisible(true);
          }
          return;
        } catch {
          // 連続失敗は復旧不能な状態として既存の異常処理に合流する。
        }
      }

      clearRoomCacheAndGoTop('エラーが発生しました。トップページに戻ります。このエラーが続く場合はお手数ですが管理者までご連絡ください。');
    }
  };

  useEffect(() => {
    if (!event?.id) return;
    fetchUsers().then((data) => {
      const users = data?.all;
      if (Array.isArray(users)) setParticipantsCount(users.length);
    }).catch(() => {});
  }, [event?.id]);

  useEffect(() => {
    setStartButtonsLocked(false);
    setStartErrorMessage('');
    setMessage(null);
    setOwnerConfirmResultReady(false);
  }, [round?.id, round?.number]);

  useEffect(() => {
    if (!isOwnerConfirmation || isCompletedEvent || !event?.id || !round?.starttime || savedResult !== '') {
      return;
    }

    if (phaseState?.roomDisplayPhase !== ROOM_DISPLAY_PHASE.BET_TIME) {
      return;
    }

    const roundKey = `${event.id}:${round?.id ?? round?.number}`;
    if (ownerConfirmDialogAutoOpenedKeyRef.current === roundKey) {
      return;
    }

    ownerConfirmDialogAutoOpenedKeyRef.current = roundKey;
    setOwnerConfirmRoundResultDialogVisible(true);
  }, [event?.id, isCompletedEvent, isOwnerConfirmation, phaseState?.roomDisplayPhase, round?.id, round?.number, round?.starttime, savedResult]);

  useEffect(() => {
    const isCompletedPhase = phaseState?.roomDisplayPhase === ROOM_DISPLAY_PHASE.COMPLETED;
    if (
      isCompletedPhase &&
      event?.id &&
      !completedDialogOpenedRef.current &&
      !completedDialogDismissedRef.current
    ) {
      completedDialogOpenedRef.current = true;
      setSaveRoundResultDialogVisible(false);
      setOwnerConfirmRoundResultDialogVisible(false);
      setConfirmRoundResultDialogVisible(false);
      setResultCorrectionDialogVisible(false);
      setCheckEventCheckOutDialogVisible(false);
      setPlannedRoundDecisionDialogVisible(false);
      setOwnerCheckoutSummaryDialogVisible(true);
    }

    if (!isCompletedPhase) {
      completedDialogOpenedRef.current = false;
      completedDialogDismissedRef.current = false;
    }
  }, [event?.id, phaseState?.roomDisplayPhase]);

  useEffect(() => {
    const commandPhase = getServerPhaseCommandFromSseData(sseData);

    switch (commandPhase) {
      case SERVER_PHASE.LOCKED:
        setRoundTransitionNotice(null);
        refresh();
        openSaveRoundResultDialog(true);
        break;
      case SERVER_PHASE.RESULT_PENDING:
        refresh();
        setSaveRoundResultDialogVisible(false);
        setOwnerConfirmRoundResultDialogVisible(false);
        setConfirmRoundResultDialogVisible(true);
        break;
      case SERVER_PHASE.CONFIRMED:
        setSaveRoundResultDialogVisible(false);
        setOwnerConfirmRoundResultDialogVisible(false);
        setConfirmRoundResultDialogVisible(false);
        openOwnerResultDialog(resolveOwnerResultRoundNumber(sseData?.payload?.round_number));
        refresh();
        break;
      case SERVER_PHASE.COMPLETED:
        setSaveRoundResultDialogVisible(false);
        setOwnerConfirmRoundResultDialogVisible(false);
        setConfirmRoundResultDialogVisible(false);
        setResultCorrectionDialogVisible(false);
        setCheckEventCheckOutDialogVisible(false);
        setPlannedRoundDecisionDialogVisible(false);
        setOwnerResultDialogVisible(false);
        if (!completedDialogOpenedRef.current && !completedDialogDismissedRef.current) {
          completedDialogOpenedRef.current = true;
          setOwnerCheckoutSummaryDialogVisible(true);
        }
        break;
      default:
        break;
    }
  }, [sseData]);

  useEffect(() => {
    if (!shouldShowPlannedRoundDecision || !plannedRoundDecisionKey) {
      return;
    }

    if (plannedDecisionDismissedKeyRef.current === plannedRoundDecisionKey) {
      return;
    }

    setOwnerResultDialogVisible(false);
    setPlannedRoundDecisionDialogVisible(true);
  }, [plannedRoundDecisionKey, shouldShowPlannedRoundDecision]);

  const handleStartRequest = () => {
    setStartButtonsLocked(true);
    setStartErrorMessage('');
    setMessage(null);
    setRoundTransitionNotice(null);
  };

  const handleStartFailed = (nextMessage) => {
    setStartButtonsLocked(false);
    setStartErrorMessage(nextMessage || '開始処理に失敗しました。');
  };

  const handleStarted = async () => {
    setMessage({ severity: 'success', text: 'ベット受付を開始しました。' });
    setRoundTransitionNotice(null);
    await refresh();
  };

  const handleDialogClose = (setter) => {
    setter(false);
    setFinishButtonResetKey((prev) => prev + 1);
  };

  const openOwnerCheckoutSummaryDialog = () => {
    completedDialogDismissedRef.current = false;
    completedDialogOpenedRef.current = true;
    setOwnerCheckoutSummaryDialogVisible(true);
  };

  const closeOwnerCheckoutSummaryDialog = () => {
    completedDialogDismissedRef.current = true;
    setOwnerCheckoutSummaryDialogVisible(false);
  };

  const scrollToCheckoutSection = () => {
    setCheckoutExpanded(true);
    window.requestAnimationFrame(() => {
      checkoutSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  };

  const handlePlannedRoundCheckout = () => {
    plannedDecisionDismissedKeyRef.current = plannedRoundDecisionKey;
    setPlannedRoundDecisionDialogVisible(false);
    setCheckoutCompletionReason('planned_rounds_completed');
    setCheckEventCheckOutDialogVisible(true);
    setCheckoutExpanded(true);
  };

  const handlePlannedRoundContinue = () => {
    plannedDecisionDismissedKeyRef.current = plannedRoundDecisionKey;
    setPlannedRoundDecisionDialogVisible(false);
  };

  const handleChatSubmit = async (submitEvent) => {
    submitEvent.preventDefault();
    if (!chat || chat.posting || chat.postCooldown) return;

    const submitted = await chat.postMessage(chatInput);
    if (submitted) setChatInput('');
  };

  const settingsRoomId = event?.room_id || roomId;
  const trimmedChatInput = chatInput.trim();
  const chatInputTooLong = trimmedChatInput.length > CHAT_MESSAGE_MAX_LENGTH;
  const chatSubmitDisabled = !event?.id || !trimmedChatInput || chatInputTooLong || chat?.posting || chat?.postCooldown;
  const chatSubmitLabel = chat?.posting ? '送信中...' : chat?.postCooldown ? '少し待ってね' : '投稿';
  const ownerConsoleDialogMode = mobile ? DIALOG_MODE.FIXED : DIALOG_MODE.SCROLL_CONTAINER;
  const ownerConsoleDialogProps = {
    dialogMode: ownerConsoleDialogMode,
    containerRef: ownerConsoleDialogMode === DIALOG_MODE.SCROLL_CONTAINER ? controlAreaRef : undefined,
  };
  const ownerConfirmCanSubmit = Boolean(
    event?.bet_close_mode === 'manual' ||
    ownerConfirmResultReady ||
    savedResult !== ''
  );

  const totalBet = Number(liveSummary?.totalBet ?? 0);
  const betBySelection = Array.isArray(liveSummary?.betBySelection) ? liveSummary.betBySelection : [];
  const resultLabel = savedResult !== '' ? choices[Number(savedResult)] || `結果 ${savedResult}` : '未登録';
  const startDisabledByOwnerRoomRule =
    isCompletedEvent ||
    round?.answers == '[]' ||
    (round?.starttime !== null && round?.starttime !== undefined && round?.starttime !== '') ||
    startButtonsLocked;

  const startControls = event && round ? (
    <Stack spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
      {isCompletedEvent ? (
        <Alert severity="info" sx={{ width: '100%' }}>
          ルームは解散済みのため、次のラウンドは開始できません。
        </Alert>
      ) : null}
      <BetStartButton
        event={event}
        round={round}
        onStart={handleStarted}
        onRequestStart={handleStartRequest}
        onRequestFailed={handleStartFailed}
        sharedDisabled={startDisabledByOwnerRoomRule}
      />
    </Stack>
  ) : null;

  const nextActionGuide = getNextActionGuide(category);
  const phaseHelper = getPhaseHelper(category);
  const showBetInfo = shouldShowBetInfo(category);
  const showResultInfo = shouldShowResultInfo(category);

  const finishControl = event && round ? (
    <RoundFinishButton
      event={event}
      round={round}
      updateSaveRoundResultDialogVisible={openSaveRoundResultDialog}
      updateConfirmRoundResultDialogVisible={setConfirmRoundResultDialogVisible}
      resetDisabledKey={finishButtonResetKey}
    />
  ) : null;

  const checkoutNavigation = (
    <Box sx={{ mt: 1.25, pt: 1.25, borderTop: '1px solid #e2e8f0' }}>
      <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
        次ラウンドを開始しない場合は、ルーム終了手続きへ進めます。
      </Typography>
      <Button
        color="error"
        onClick={scrollToCheckoutSection}
        variant="outlined"
        sx={{ fontWeight: 900 }}
      >
        ルームを終了する
      </Button>
    </Box>
  );

  const actionPanel = (() => {
    if (isCompletedEvent) {
      return (
        <Stack spacing={1}>
          <Alert severity="info">
            ルームは解散済みのため、次のラウンドは開始できません。
          </Alert>
          <Button href="/" variant="contained">
            TOPへ戻る
          </Button>
          <Button variant="outlined" onClick={openOwnerCheckoutSummaryDialog}>
            最終結果を表示
          </Button>
        </Stack>
      );
    }

    switch (category) {
      case OWNER_CONSOLE_DISPLAY_CATEGORY.WAITING:
        return startControls;
      case OWNER_CONSOLE_DISPLAY_CATEGORY.ROUND_RESULT:
        return (
          <Stack spacing={1}>
            {startControls}
            {checkoutNavigation}
          </Stack>
        );
      case OWNER_CONSOLE_DISPLAY_CATEGORY.BETTING:
        return (
          <Stack spacing={1}>
            {finishControl}
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              {event?.bet_close_mode === 'manual'
                ? '結果登録を開始するとベット受付を終了します。'
                : '終了時刻経過後に結果登録ボタンが有効になります。'}
            </Typography>
          </Stack>
        );
      case OWNER_CONSOLE_DISPLAY_CATEGORY.RESULT_INPUT:
      case OWNER_CONSOLE_DISPLAY_CATEGORY.RESULT_REVIEW:
        return finishControl;
      default:
        return <Typography variant="body2" sx={{ color: '#64748b' }}>イベントまたはラウンド状態を取得中です。</Typography>;
    }
  })();

  const headerSection = (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="overline" sx={{ color: '#0f766e', letterSpacing: '0.16em', fontWeight: 900 }}>{mobile ? 'SP OWNER CONTROL' : 'OWNER COMMAND DESK'}</Typography>
        <Typography variant={mobile ? 'h5' : 'h4'} fontWeight={900} sx={{ mt: 0.5 }}>オーナーコンソール</Typography>
        <Typography variant="body2" sx={{ mt: 1, color: '#475569', maxWidth: 720 }}>ここはベットルームの運営操作するオーナー画面です。</Typography>
      </Box>
      <HelpButton
        staticHelpMessageKey={mobile ? '/owner-console-mobile' : '/owner-console'}
        iconButtonSx={{
          color: '#0f172a',
          bgcolor: 'rgba(255, 255, 255, 0.72)',
          border: '1px solid rgba(15, 23, 42, 0.12)',
          flexShrink: 0,
          '&:hover': {
            bgcolor: '#fff',
          },
        }}
      />
    </Box>
  );

  const settingsSection = (
    <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: '#eef2ff', border: '1px solid #c7d2fe' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={900}>設定変更</Typography>
          <Typography variant="body2" sx={{ color: '#475569' }}>ルーム設定の確認や変更ができます。</Typography>
        </Box>
        <Button
          href={settingsRoomId ? `/owner?id=${settingsRoomId}` : undefined}
          disabled={!settingsRoomId}
          variant="outlined"
          sx={{ minHeight: 44, fontWeight: 900, whiteSpace: 'nowrap' }}
        >
          設定変更へ
        </Button>
      </Stack>
    </Box>
  );

  const statusSummarySection = (
    <Box
      sx={{
        p: mobile ? 1.75 : 1.25,
        borderRadius: 3,
        bgcolor: mobile ? '#ecfeff' : '#f8fafc',
        border: mobile ? '1px solid #a5f3fc' : '1px solid #cbd5e1',
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
        <Box>
          <Typography variant="caption" sx={{ color: mobile ? '#0e7490' : '#64748b', fontWeight: 900 }}>
            {mobile ? '現在状態サマリー' : '状態'}
          </Typography>
          <Typography variant={mobile ? 'h6' : 'subtitle1'} fontWeight={900}>
            {OWNER_CONSOLE_DISPLAY_LABEL[category]}
          </Typography>
          {mobile ? (
            <Typography variant="body2" sx={{ color: '#0f766e', mt: 0.5 }}>{phaseHelper}</Typography>
          ) : null}
        </Box>
        <Box
          sx={{
            alignSelf: { xs: 'flex-start', sm: 'center' },
            border: mobile ? '1px solid #67e8f9' : '1px solid #cbd5e1',
            borderRadius: 1.5,
            bgcolor: mobile ? '#cffafe' : '#fff',
            color: mobile ? '#0e7490' : '#475569',
            cursor: 'default',
            px: 1,
            py: 0.5,
            userSelect: 'none',
          }}
        >
          <Typography variant="caption" fontWeight={900}>
            {`ROUND ${round?.number ?? event?.round ?? '-'}`}
          </Typography>
        </Box>
      </Stack>
      {mobile ? (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mt: 1.5 }}>
          <Chip label={`参加者 ${participantsCount ?? event?.participants_count ?? '-'}人`} />
          <CountdownChip event={event} round={round} />
          <Chip label={`最終更新 ${lastUpdatedAt ? lastUpdatedAt.toLocaleTimeString() : '取得待ち'}`} />
        </Stack>
      ) : null}
    </Box>
  );

  const actionSection = (
    <Box
      sx={{
        p: mobile ? 1.5 : 1.5,
        borderRadius: 3,
        bgcolor: '#fff',
        border: mobile ? '1px solid #cbd5e1' : '2px solid #2563eb',
        boxShadow: mobile ? '0 10px 24px rgba(15, 23, 42, 0.06)' : '0 16px 30px rgba(37, 99, 235, 0.16)',
      }}
    >
      <Stack spacing={1}>
        <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#fff7ed', border: '1px solid #fed7aa' }}>
          <Typography variant="caption" sx={{ color: '#c2410c', fontWeight: 900 }}>次にやること</Typography>
          <Typography sx={{ mt: 0.25, fontSize: '0.75rem', lineHeight: 1.45, fontWeight: 700, overflowWrap: 'anywhere' }}>{nextActionGuide}</Typography>
        </Box>

        <Box sx={{ borderTop: '1px solid #e2e8f0', pt: 1 }}>
          <Typography variant="subtitle1" fontWeight={900}>主操作</Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>進行で使う操作が表示されます。</Typography>
          {actionPanel}
        </Box>
      </Stack>
    </Box>
  );

  const chatSection = (
    <Box sx={{ p: 1.75, borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #cbd5e1' }}>
      <Typography variant="subtitle1" fontWeight={900}>チャット投稿</Typography>
      <Typography variant="body2" sx={{ color: '#64748b', mb: 1 }}>
        ルーム参加者チャットにメッセージを投稿します。
      </Typography>
      <Box component="form" onSubmit={handleChatSubmit} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
        <TextField
          disabled={!event?.id || chat?.posting}
          error={chatInputTooLong}
          fullWidth
          helperText={`${trimmedChatInput.length}/${CHAT_MESSAGE_MAX_LENGTH}${chatInputTooLong ? ' 文字以内で入力してください' : ''}`}
          inputProps={{ maxLength: CHAT_MESSAGE_MAX_LENGTH + 20 }}
          multiline
          maxRows={3}
          minRows={mobile ? 2 : 1}
          onChange={(inputEvent) => setChatInput(inputEvent.target.value)}
          placeholder="チャットを入力"
          size="small"
          value={chatInput}
        />
        <Button
          disabled={chatSubmitDisabled}
          type="submit"
          variant="contained"
          sx={{ minWidth: { xs: '100%', sm: 104 }, alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
        >
          {chatSubmitLabel}
        </Button>
      </Box>
      {chat?.postSuccessMessage ? <Alert severity="success" sx={{ mt: 1 }}>{chat.postSuccessMessage}</Alert> : null}
      {chat?.postError ? <Alert severity="error" sx={{ mt: 1 }}>{chat.postError}</Alert> : null}
      {chat?.fetchError ? <Alert severity="warning" sx={{ mt: 1 }}>{chat.fetchError}</Alert> : null}
      {Array.isArray(chat?.messages) && chat.messages.length > 0 ? (
        <Typography variant="body2" sx={{ mt: 1, color: '#475569', overflowWrap: 'anywhere' }}>
          最新：{chat.messages[chat.messages.length - 1]?.user_name || 'ユーザー'}「{chat.messages[chat.messages.length - 1]?.message}」
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ mt: 1, color: '#64748b' }}>まだ投稿されたチャットはありません。</Typography>
      )}
    </Box>
  );

  const roundInfoSection = (
    <Box>
      <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1 }}>ラウンド情報</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
        <InfoCard label="現在フェーズ" value={OWNER_CONSOLE_DISPLAY_LABEL[category]} tone="accent" />
        <InfoCard label="ラウンド番号" value={round?.number ?? event?.round ?? '-'} />
        <InfoCard label="参加者数" value={`${participantsCount ?? event?.participants_count ?? '-'}人`} />
        <CountdownInfoCard event={event} round={round} />
      </Box>
    </Box>
  );

  const betInfoSection = showBetInfo ? (
    <Box>
      <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1 }}>ベット状況</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
        <InfoCard label="総ベット" value={formatPoint(totalBet)} helper={betBySelection.length ? '' : '取得待ちまたは未ベット'} />
        <InfoCard label="選択肢別状況" value={betBySelection.length ? `${betBySelection.length}件` : '取得待ち'}/>
      </Box>
      {betBySelection.length > 0 ? (
        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
          {betBySelection.map((item) => {
            const index = Number(item.selection);
            const amount = Number(item.totalBet || 0);
            return <Typography key={item.selection} variant="body2" sx={{ mt: 0.5 }}>{choices[index] || `選択肢 ${index + 1}`}：{formatPoint(amount)}</Typography>;
          })}
        </Box>
      ) : null}
    </Box>
  ) : null;

  const resultInfoSection = showResultInfo ? (
    <Box>
      <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1 }}>結果・確認状況</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
        <InfoCard label="登録結果" value={resultLabel} helper={review?.review_status === 'held' ? '再確認依頼により保留中' : '未登録の場合は結果登録が必要です'} />
        <InfoCard label="確認対象人数" value={review?.dispute_eligible_user_count ?? '-'} />
        <InfoCard label="確認済み人数" value={review?.confirmed_user_count ?? review?.reviewed_user_count ?? '-'} />
        <InfoCard label="再確認依頼" value={`${review?.dispute_count ?? 0}件`} />
      </Box>
    </Box>
  ) : null;

  const checkoutSection = (
    <Box id="owner-checkout-section" ref={checkoutSectionRef}>
      <Accordion
        disableGutters
        expanded={checkoutExpanded}
        onChange={(_, expanded) => setCheckoutExpanded(expanded)}
        sx={{ borderRadius: 3, border: '1px solid #fecaca', overflow: 'hidden', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#fff1f2' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={900}>ルームを解散する</Typography>
            <Typography variant="body2" sx={{ color: '#9f1239' }}>誤操作防止のため折りたたんでいます。</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ bgcolor: '#fff', p: 1.5 }}>
          <Stack spacing={1.25}>
            <Typography variant="body2" sx={{ color: '#9f1239' }}>ルームを解散すると、参加者は最終結果を確認できます。次のラウンドを開始できなくなり、解散後はルームを復活させることはできません。</Typography>
            {event && round ? (
              <EventCheckOutButton
                event={event}
                round={round}
                dialogVisible={checkEventCheckOutDialogVisible}
                updateDialogVisible={setCheckEventCheckOutDialogVisible}
              />
            ) : null}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );

  const operationMessageSection = (
    <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
      <Typography variant="body2" sx={{ color: '#475569' }}>操作結果メッセージはこの操作卓上部に表示されます。最終更新：{lastUpdatedAt ? lastUpdatedAt.toLocaleTimeString() : '取得待ち'}</Typography>
    </Box>
  );

  const sections = {
    header: headerSection,
    settings: settingsSection,
    message: (
      <>
        {roundTransitionNotice ? <Alert severity={roundTransitionNotice.severity}>{roundTransitionNotice.text}</Alert> : null}
        {message ? <Alert severity={message.severity} sx={{ mt: roundTransitionNotice ? 1 : 0 }}>{message.text}</Alert> : null}
      </>
    ),
    startError: startErrorMessage ? <Alert severity="error">{startErrorMessage}</Alert> : null,
    statusSummary: statusSummarySection,
    action: actionSection,
    chat: chatSection,
    roundInfo: roundInfoSection,
    betInfo: betInfoSection,
    resultInfo: resultInfoSection,
    checkout: checkoutSection,
    operationMessage: operationMessageSection,
  };

  return (
    <Box ref={controlAreaRef} component="main" sx={{ gridArea: 'control', position: 'relative', minHeight: 0, p: { xs: 2, md: 2.5 }, borderRadius: { xs: 0, md: 3 }, bgcolor: mobile ? '#f8fafc' : 'rgba(248, 250, 252, 0.94)', border: mobile ? 'none' : '1px solid rgba(15, 23, 42, 0.14)', color: '#0f172a', overflow: 'auto' }}>
      {mobile ? (
        <OwnerControlAreaMobileLayout sections={sections} />
      ) : (
        <OwnerControlAreaDesktopLayout sections={sections} />
      )}

      {event && round ? (
        <>
          <SaveRoundResultDialog
            event={event}
            isVisible={saveRoundResultDialogVisible}
            onClose={() => handleDialogClose(setSaveRoundResultDialogVisible)}
            round={round}
            nextRoundStart={refresh}
            {...ownerConsoleDialogProps}
          />
          <OwnerConfirmRoundResultDialog
            event={event}
            canSubmit={ownerConfirmCanSubmit}
            isVisible={ownerConfirmRoundResultDialogVisible}
            onClose={() => handleDialogClose(setOwnerConfirmRoundResultDialogVisible)}
            round={round}
            onConfirmed={refresh}
            {...ownerConsoleDialogProps}
          />
          <ConfirmRoundResultDialog
            isVisible={confirmRoundResultDialogVisible}
            onClose={() => handleDialogClose(setConfirmRoundResultDialogVisible)}
            round={round}
            refreshKey={reviewRefreshKey}
            onEdit={() => setResultCorrectionDialogVisible(true)}
            onConfirmed={refresh}
            {...ownerConsoleDialogProps}
          />
          <ResultCorrectionDialog
            isVisible={resultCorrectionDialogVisible}
            onClose={() => handleDialogClose(setResultCorrectionDialogVisible)}
            round={round}
            onCorrected={refresh}
            {...ownerConsoleDialogProps}
          />
          <CheckEventCheckOutDialog
            event={event}
            round={round}
            isVisible={checkEventCheckOutDialogVisible}
            onClose={() => {
              setCheckEventCheckOutDialogVisible(false);
              setCheckoutCompletionReason('owner_ended');
            }}
            completionReason={checkoutCompletionReason}
            {...ownerConsoleDialogProps}
          />
          <PlannedRoundDecisionDialog
            isVisible={plannedRoundDecisionDialogVisible}
            plannedRoundCount={plannedRoundCount}
            onCheckout={handlePlannedRoundCheckout}
            onContinue={handlePlannedRoundContinue}
            {...ownerConsoleDialogProps}
          />
          <OwnerCheckoutSummaryDialog
            event={event}
            isVisible={ownerCheckoutSummaryDialogVisible}
            onClose={closeOwnerCheckoutSummaryDialog}
            {...ownerConsoleDialogProps}
          />
          <OwnerResultDialog
            isVisible={ownerResultDialogVisible}
            dialogDatas={ownerResultDialogDatas}
            onClose={() => setOwnerResultDialogVisible(false)}
            {...ownerConsoleDialogProps}
          />
        </>
      ) : null}
    </Box>
  );
}
